import { useMutation, useQuery } from "@apollo/client";
import { isEqual } from "lodash-es";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  BULK_UPDATE_OPERATING_HOURS,
  GET_SALON_OPERATING_HOURS,
  INITIALIZE_DEFAULT_OPERATING_HOURS,
} from "@/graphql/configuration";
import { BULK_UPDATE_SALON_CONTACTS, GET_SALON, UPDATE_SALON_BASIC_INFO } from "@/graphql/salon";
import type { SalonContactInfo } from "@/lib/store/salon";
import { SalonContactType, useSalonStore } from "@/lib/store/salon";
import type { BusinessSettingsFormData } from "../features/business/type/business-settings";
import { listValidationIssues } from "../features/business/utils/validation-issues";
import type { DaySchedule } from "../types";

const INVALID_PHONE_NUMBER = "INVALID_PHONE_NUMBER";

function getInvalidPhoneError(error: unknown): { value: string; message: string } | null {
  const extensions = (
    error as {
      graphQLErrors?: Array<{
        extensions?: {
          code?: string;
          fieldErrors?: Record<string, string>;
          metadata?: { value?: unknown };
        };
      }>;
    }
  )?.graphQLErrors?.[0]?.extensions;
  const value = extensions?.metadata?.value;
  const message = extensions?.fieldErrors?.contactValue;

  if (extensions?.code !== INVALID_PHONE_NUMBER || typeof value !== "string" || !message) {
    return null;
  }
  return { value, message };
}

export function useBusinessSettings() {
  const { t } = useTranslation("settings");
  const { currentSalon } = useSalonStore();
  const salonId = currentSalon?.id;

  // State for mutation errors and loading
  const [mutationError, setMutationError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(false);

  // GraphQL queries
  const {
    data: salonData,
    loading: salonLoading,
    error: salonError,
    refetch: refetchSalon,
  } = useQuery(GET_SALON, {
    variables: { salonId: salonId ?? "" },
    skip: !salonId,
  });

  const {
    data: hoursData,
    loading: hoursLoading,
    error: hoursError,
    refetch: refetchHours,
  } = useQuery(GET_SALON_OPERATING_HOURS, {
    variables: { salonId: salonId ?? "" },
    skip: !salonId,
  });

  // GraphQL mutations with error handling
  const [updateSalonBasicInfo, { error: updateBasicInfoError }] =
    useMutation(UPDATE_SALON_BASIC_INFO);
  const [bulkUpdateSalonContacts, { error: updateContactsError, reset: resetContactsMutation }] =
    useMutation(BULK_UPDATE_SALON_CONTACTS);
  const [bulkUpdateOperatingHours, { error: updateHoursError }] = useMutation(
    BULK_UPDATE_OPERATING_HOURS
  );
  const [initializeDefaultHours, { error: initializeHoursError }] = useMutation(
    INITIALIZE_DEFAULT_OPERATING_HOURS
  );

  // Transform GraphQL data to form data
  const defaultValues: BusinessSettingsFormData = useMemo(() => {
    const salon = salonData?.getSalon;
    const hours = hoursData?.getSalonOperatingHours;
    const contacts = salon?.contactInfo || [];

    return {
      basicInfo: {
        name: salon?.name || "",
        address: salon?.address || "",
        additionalAddressInfo: salon?.additionalAddressInfo || "",
        city: salon?.city || "",
        state: salon?.state || "",
        zipCode: salon?.zipCode || "",
        country: salon?.country || "",
      },
      contactInfo: {
        emails: contacts.filter((c) => c.type === SalonContactType.EMAIL) as SalonContactInfo[],
        phones: contacts.filter((c) => c.type === SalonContactType.PHONE) as SalonContactInfo[],
      },
      operatingHours: {
        weeklyHours: hours?.weeklyHours || [],
      },
    };
  }, [salonData, hoursData]);

  // Create unified form. Use `values` so RHF auto-syncs the form with server
  // data as it loads/refreshes; `keepDirtyValues` preserves user edits during
  // background Apollo cache refreshes. `keepDirty` must be set alongside it —
  // on its own, `keepDirtyValues` keeps the edited values but still resets
  // `isDirty` to false, which silently disables the Save button whenever a
  // background refetch lands while the user is typing.
  const form = useForm<BusinessSettingsFormData>({
    values: defaultValues,
    resetOptions: { keepDirtyValues: true, keepDirty: true },
  });

  // Update business profile
  const updateBusinessProfile = useCallback(
    async (formData: BusinessSettingsFormData, originalData: BusinessSettingsFormData) => {
      if (!salonId) return;

      const updates: Promise<unknown>[] = [];

      // Update salon basic info if changed
      const basicInfoChanged = !isEqual(formData.basicInfo, originalData.basicInfo);

      if (basicInfoChanged) {
        updates.push(
          updateSalonBasicInfo({
            variables: {
              salonId,
              input: formData.basicInfo,
            },
          })
        );
      }

      // Handle contact info changes with bulk update
      const contactsChanged = !isEqual(formData.contactInfo, originalData.contactInfo);

      if (contactsChanged) {
        // Combine emails and phones into a single array
        const allContacts = [...formData.contactInfo.emails, ...formData.contactInfo.phones];

        const contacts = allContacts.map((contact) => ({
          id: contact.id.startsWith("temp-") ? undefined : contact.id,
          type: contact.type,
          value: contact.value,
          label: contact.label,
          order: contact.order,
        }));

        updates.push(
          bulkUpdateSalonContacts({
            variables: {
              salonId,
              input: { contacts },
            },
          })
        );
      }

      await Promise.all(updates);
    },
    [salonId, updateSalonBasicInfo, bulkUpdateSalonContacts]
  );

  // Update opening hours
  const updateOpeningHours = useCallback(
    async (weeklyHours: DaySchedule[]) => {
      if (!salonId || weeklyHours.length === 0) return;

      const operatingHours = weeklyHours.map((day) => ({
        dayOfWeek: day.dayOfWeek,
        timeSlots: day.timeSlots.map((slot) => ({
          startTime: slot.startTime,
          endTime: slot.endTime,
          slotOrder: slot.slotOrder,
        })),
      }));

      await bulkUpdateOperatingHours({
        variables: {
          salonId,
          input: { operatingHours },
        },
      });
    },
    [salonId, bulkUpdateOperatingHours]
  );

  // Main save function - handles all updates atomically
  const save = useCallback(async () => {
    const formData = form.getValues();
    const originalData = defaultValues;

    // Diff the form against the server data instead of reading
    // `form.formState.isDirty`. `formState` is a Proxy whose getters are
    // impure (reading a key registers a subscription), so React Compiler can
    // memoize the access away inside this callback and freeze the value at
    // `false` — which made every save bail out with "No changes to save".
    // These flags are the same ones the mutations below already branch on.
    const businessProfileChanged =
      !isEqual(formData.basicInfo, originalData.basicInfo) ||
      !isEqual(formData.contactInfo, originalData.contactInfo);
    const hoursChanged = !isEqual(formData.operatingHours, originalData.operatingHours);

    if (!businessProfileChanged && !hoursChanged) {
      toast.info("No changes to save");
      return;
    }

    // Validate salon name before attempting to save
    const isValid = await form.trigger(
      ["basicInfo.name", "contactInfo.emails", "contactInfo.phones"],
      // Bring the first invalid field into view; it may be far above the edited section
      { shouldFocus: true }
    );
    if (!isValid) {
      // Name the first field to fix and why; the others are counted
      const [first, ...rest] = listValidationIssues(form, {
        name: t("business.businessName"),
        email: t("business.email"),
        phone: t("business.phone"),
      });
      if (first) {
        toast.error(t("business.validation.saveBlocked", { field: first.field }), {
          description:
            rest.length > 0
              ? `${first.message}. ${t("business.validation.more", { count: rest.length })}`
              : first.message,
        });
      }
      return;
    }

    setSaving(true);
    setMutationError(null);
    form.clearErrors("contactInfo.phones");

    try {
      const updates: Promise<unknown>[] = [];

      if (businessProfileChanged) {
        updates.push(updateBusinessProfile(formData, originalData));
      }

      if (hoursChanged) {
        updates.push(updateOpeningHours(formData.operatingHours.weeklyHours));
      }

      // Execute all updates atomically
      await Promise.all(updates);

      // Refetch data to ensure UI is up to date
      const refetchPromises: Promise<unknown>[] = [];
      if (businessProfileChanged) {
        refetchPromises.push(refetchSalon());
      }
      if (hoursChanged) {
        refetchPromises.push(refetchHours());
      }

      await Promise.all(refetchPromises);

      // Reset form to clean state
      form.reset(form.getValues());

      toast.success("Business settings saved successfully");
    } catch (error) {
      const invalidPhone = getInvalidPhoneError(error);
      const invalidRows = invalidPhone
        ? formData.contactInfo.phones.flatMap((phone, index) =>
            phone.value === invalidPhone.value ? [index] : []
          )
        : [];

      if (invalidPhone && invalidRows.length > 0) {
        resetContactsMutation();
        for (const index of invalidRows) {
          form.setError(`contactInfo.phones.${index}.value`, {
            type: "server",
            message: invalidPhone.message,
          });
        }
        toast.error(invalidPhone.message);
        throw error;
      }

      const mutationErr = error instanceof Error ? error : new Error("Save failed");
      setMutationError(mutationErr);
      const errorMessage = mutationErr.message || "Unknown error";
      toast.error(`Save failed: ${errorMessage}`);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [
    form,
    defaultValues,
    updateBusinessProfile,
    updateOpeningHours,
    refetchSalon,
    refetchHours,
    resetContactsMutation,
    t,
  ]);

  // Discard changes — reset to form's internal defaults (last-known-good state
  // set by initial load or last successful save), not the latest useMemo result
  // which may differ after Apollo cache writes.
  const discard = useCallback(() => {
    form.reset();
    setMutationError(null);
    toast.info("Changes discarded");
  }, [form]);

  // Initialize default hours
  const initializeDefaults = useCallback(async () => {
    if (!salonId) return;

    try {
      await initializeDefaultHours({ variables: { salonId } });
      await refetchHours();
      toast.success("Default hours initialized");
    } catch {
      toast.error("Failed to initialize default hours");
    }
  }, [salonId, initializeDefaultHours, refetchHours]);

  // Combine all errors
  const combinedError =
    salonError ||
    hoursError ||
    mutationError ||
    updateBasicInfoError ||
    updateContactsError ||
    updateHoursError ||
    initializeHoursError;

  return {
    // Form instance
    form,

    // State
    loading: salonLoading || hoursLoading, // Only query loading, not mutation loading
    saving, // Separate saving state for mutations
    error: combinedError,

    // Actions
    save,
    discard,
    initializeDefaults,
  };
}
